import { MUHASABAH_JALAN, MUHASABAH_MAX_CHARS } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { EmptyState } from '../../components/EmptyState.tsx'
import { ReminderTime } from '../../components/ReminderTime.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { ApiError } from '../../lib/api.ts'
import { PUSH_OUTCOME_MSG, enablePush } from '../../lib/push.ts'
import { useGarden } from './LevelUp.tsx'
import { Back, HadisBlock } from './parts.tsx'
import { JALAN_LABEL, dayLong, putMuhasabahReminder, saveMuhasabah, useMuhasabah, type Jalan, type Reminder } from './perjalanan.ts'

/** The evening reminder (push), off until turned on. Turning it on also asks this browser for notifications. */
function ReminderCard({ r }: { r: Reminder }) {
  const qc = useQueryClient()
  const [cur, setCur] = useState(r)
  const [note, setNote] = useState('')
  const save = async (next: Reminder) => {
    setNote('')
    const prev = cur
    setCur(next)
    try {
      if (next.enabled && !prev.enabled) {
        const out = await enablePush()
        if (out !== 'on') setNote(PUSH_OUTCOME_MSG[out])
      }
      await putMuhasabahReminder(next)
      void qc.invalidateQueries({ queryKey: ['perjalanan', 'muhasabah'] })
    } catch (e) {
      setCur(prev)
      setNote(e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')
    }
  }
  return (
    <section className="card pj-sec reveal" aria-labelledby="mh-rem-h">
      <div className="set-row" style={{ padding: 0 }}>
        <b id="mh-rem-h" style={{ flex: 1 }}>Ingatkan aku tiap malam</b>
        <button type="button" role="switch" aria-checked={cur.enabled} aria-labelledby="mh-rem-h" className="switch" onClick={() => void save({ ...cur, enabled: !cur.enabled })} />
      </div>
      {cur.enabled && <ReminderTime hour={cur.hour} minute={cur.minute} onChange={(hour, minute) => void save({ ...cur, hour, minute })} />}
      <p className="help" role="status">{note || 'Notifikasi tidak dikirim kalau muhasabah hari itu sudah kamu isi.'}</p>
    </section>
  )
}

/**
 * /perjalanan/muhasabah — "Muhasabah 60 detik" (docs/PRD.md §6.12): did today's small step happen, one thing to be
 * grateful for, one thing to do better. Private. Then a Sahih hadith about going to bed, from the dataset.
 */
export function MuhasabahPage() {
  const { data, isPending, isError, refetch } = useMuhasabah()
  const { after, overlay } = useGarden()
  const [jalan, setJalan] = useState<Jalan | null>(null)
  const [syukur, setSyukur] = useState('')
  const [perbaiki, setPerbaiki] = useState('')
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [error, setError] = useState('')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!data || loaded) return
    setJalan(data.today?.jalan ?? null)
    setSyukur(data.today?.syukur ?? '')
    setPerbaiki(data.today?.perbaiki ?? '')
    setLoaded(true)
  }, [data, loaded])

  const save = async () => {
    setError('')
    setState('saving')
    try {
      after((await saveMuhasabah({ jalan, syukur: syukur.trim() || null, perbaiki: perbaiki.trim() || null })).garden)
      setState('saved')
    } catch (e) {
      setError(e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')
      setState('idle')
    }
  }

  return (
    <div className="page pj">
      <Back />
      <h1>Muhasabah 60 detik</h1>
      <p className="muted" style={{ marginTop: -6 }}>Menengok hari ini sebentar sebelum tidur. Hanya kamu yang bisa membaca jawabanmu.</p>

      {isPending && <SkCard label="Memuat muhasabah" lines={5} style={{ marginTop: 14 }} />}
      {isError && (
        <EmptyState obj="bulan" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && (
        <>
          <section className="card pj-sec mh-form reveal">
            <fieldset>
              <legend><b>1.</b> Langkah kecil yang kamu niatkan hari ini, berjalan?</legend>
              <div className="seg" role="group">
                {MUHASABAH_JALAN.map((j) => (
                  <button key={j} type="button" aria-pressed={jalan === j} onClick={() => { setJalan(j); setState('idle') }}>{JALAN_LABEL[j]}</button>
                ))}
              </div>
            </fieldset>
            <label className="field">
              <span><b>2.</b> Satu hal yang kamu syukuri hari ini</span>
              <input className="input" value={syukur} maxLength={MUHASABAH_MAX_CHARS} onChange={(e) => { setSyukur(e.target.value); setState('idle') }} placeholder="Misalnya: bisa makan bersama keluarga" />
            </label>
            <label className="field">
              <span><b>3.</b> Satu hal yang ingin kamu perbaiki besok</span>
              <input className="input" value={perbaiki} maxLength={MUHASABAH_MAX_CHARS} onChange={(e) => { setPerbaiki(e.target.value); setState('idle') }} placeholder="Misalnya: tidak menunda shalat Isya" />
            </label>
            <p className="error" role="alert">{error}</p>
            <button type="button" className="btn btn-primary btn-block" onClick={save} disabled={state === 'saving' || !jalan}>
              {state === 'saving' ? 'Menyimpan…' : state === 'saved' ? 'Tersimpan' : 'Simpan muhasabah'}
            </button>
            {!jalan && <p className="help" style={{ textAlign: 'center' }}>Jawab pertanyaan pertama dulu.</p>}
            {state === 'saved' && <p className="help" role="status" style={{ textAlign: 'center' }}>Terima kasih sudah menengok harimu. Selamat beristirahat.</p>}
          </section>

          <ReminderCard r={data.pengingat} />

          {data.hadis && (
            <section className="card pj-sec reveal" aria-labelledby="mh-hadis-h">
              <h2 id="mh-hadis-h">Sebelum tidur</h2>
              <HadisBlock h={data.hadis} />
            </section>
          )}

          {data.syukurLalu.length > 0 && (
            <section className="pj-sec-plain reveal" aria-labelledby="mh-lalu-h">
              <h2 className="section-t" id="mh-lalu-h">Syukurmu yang lalu</h2>
              <ul className="card mh-past">
                {data.syukurLalu.map((s) => <li key={s.day}><small>{dayLong(s.day)}</small>{s.text}</li>)}
              </ul>
            </section>
          )}
        </>
      )}
      {overlay}
    </div>
  )
}
